import { useState, useEffect } from 'react';
import useSWR from 'swr';
import { swrFetcher } from '../services/apiClient';

/**
 * Hook to detect if page is visible (using Page Visibility API)
 */
function usePageVisibility() {
  const [isVisible, setIsVisible] = useState(!document.hidden);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return isVisible;
}

// Cache key for storing balance timestamp in localStorage
const BALANCE_CACHE_TIMESTAMP_KEY = 'balance_cache_timestamp';
// Cache key for storing balance data in localStorage
const BALANCE_CACHE_DATA_KEY = 'balance_cache_data';

/**
 * Check if balance data contains any non-zero balances
 * Returns true if there are non-zero balances, false otherwise
 */
function filterZeroBalances(data) {
  if (!data) return false;
  
  // Check treasury balances
  if (data.treasury) {
    for (const [key, value] of Object.entries(data.treasury)) {
      const available = parseFloat(value?.available || value) || 0;
      const usdValue = parseFloat(value?.usdValue || 0) || available;
      if (usdValue > 0 || available > 0) {
        return true; // Found non-zero balance
      }
    }
  }
  
  // Check exchange balances
  if (data.exchanges) {
    for (const [exchange, assets] of Object.entries(data.exchanges)) {
      if (!assets) continue;
      for (const [asset, balance] of Object.entries(assets)) {
        const available = parseFloat(balance?.available || 0) || 0;
        const usdValue = parseFloat(balance?.usdValue || 0) || available;
        if (usdValue > 0 || available > 0) {
          return true; // Found non-zero balance
        }
      }
    }
  }
  
  // Check totals
  if (data.totals) {
    const treasuryTotal = parseFloat(data.totals?.treasury?.usd || 0) || 0;
    const exchangesTotal = parseFloat(data.totals?.exchanges?.usd || 0) || 0;
    const grandTotal = parseFloat(data.totals?.grandTotal?.usd || 0) || 0;
    if (treasuryTotal > 0 || exchangesTotal > 0 || grandTotal > 0) {
      return true; // Found non-zero total
    }
  }
  
  return false; // All balances are zero
}

/**
 * Load cached balance data from localStorage synchronously
 * This must be called outside of React hooks to ensure it's available immediately
 */
function loadCachedBalanceData() {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(BALANCE_CACHE_DATA_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Only return cached data if it has non-zero balances
      if (filterZeroBalances(parsed)) {
        return parsed;
      }
    }
  } catch (e) {
    console.error('[Balance Hook] Failed to parse cached balance data:', e);
  }
  return null;
}

export function useBalances() {
  // Load cached balance data synchronously (outside of useState) to ensure it's available immediately
  const initialCachedData = loadCachedBalanceData();
  
  // Load cached balance data and timestamp from localStorage on mount
  const [cachedData, setCachedData] = useState(initialCachedData);

  const [cacheTimestamp, setCacheTimestamp] = useState(() => {
    // Load cached timestamp from localStorage on mount
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(BALANCE_CACHE_TIMESTAMP_KEY);
      return stored ? new Date(stored) : null;
    }
    return null;
  });

  // Track if we've ever successfully fetched data
  const [hasData, setHasData] = useState(!!initialCachedData);

  // Auto-refresh on page load/mount, but no continuous polling
  // SWR v2 automatically caches data, so we'll show cached data while fetching
  const { data, error, isLoading, mutate, isValidating } = useSWR(
    '/balances',
    swrFetcher,
    {
      refreshInterval: null, // Disable continuous polling
      revalidateOnFocus: false, // Don't refresh when user switches back to tab
      revalidateOnReconnect: false, // Don't refresh when network reconnects
      dedupingInterval: 5000, // Prevent duplicate requests within 5 seconds
      revalidateIfStale: true, // Revalidate stale data on mount (page load)
      // Use cached data from localStorage as fallback on initial load
      // This ensures cached data is shown immediately on page reload
      fallbackData: initialCachedData,
      onSuccess: (data) => {
        // Update timestamp and cache data when new data arrives (fresh fetch)
        // Only cache if there are non-zero balances
        if (data) {
          // Filter out zero balances before caching
          const hasNonZeroBalances = filterZeroBalances(data);
          
          // Only update cache if there are non-zero balances
          // This prevents caching empty/zero balance states
          if (hasNonZeroBalances) {
            const now = new Date();
            setCacheTimestamp(now);
            setHasData(true);
            setCachedData(data); // Update cached data state
            if (typeof window !== 'undefined') {
              localStorage.setItem(BALANCE_CACHE_TIMESTAMP_KEY, now.toISOString());
              localStorage.setItem(BALANCE_CACHE_DATA_KEY, JSON.stringify(data));
            }
            // Log successful balance fetch
            if (typeof window !== 'undefined' && window.console) {
              console.log(`[Balance Hook] Balance fetched successfully at ${now.toISOString()}`);
            }
          } else {
            // If we get zero balances, keep showing cached non-zero data if available
            // but don't update the timestamp or cache
            console.log('[Balance Hook] Received zero balances, keeping cached data');
          }
        }
      },
    }
  );

  // If we have cached data but no timestamp yet, try to load it from localStorage
  useEffect(() => {
    if (data && !cacheTimestamp && typeof window !== 'undefined') {
      const stored = localStorage.getItem(BALANCE_CACHE_TIMESTAMP_KEY);
      if (stored) {
        setCacheTimestamp(new Date(stored));
      }
    }
    if (data) {
      setHasData(true);
    }
  }, [data, cacheTimestamp]);

  // Determine if we're showing stale/cached data
  const isStale = isValidating && hasData && !!data;

  // Prefer non-zero data: if new data is all zeros but we have cached non-zero data, use cached
  const balancesToReturn = (() => {
    if (!data && cachedData) {
      // No data from API yet, use cached data
      return cachedData;
    }
    if (data) {
      // Check if new data has non-zero balances
      const hasNonZeroInNewData = filterZeroBalances(data);
      if (hasNonZeroInNewData) {
        // New data has non-zero balances, use it
        return data;
      } else if (cachedData && filterZeroBalances(cachedData)) {
        // New data is all zeros, but we have cached non-zero data, keep cached
        return cachedData;
      }
      // Both are zero, use new data
      return data;
    }
    return data || cachedData || null;
  })();

  return {
    balances: balancesToReturn, // Prefer non-zero balances (cached or fresh)
    isLoading: isLoading && !hasData, // Only show loading if we've never fetched data
    isValidating, // True when fetching in background
    isError: error,
    error,
    refresh: mutate, // Call this to manually refresh balances
    cacheTimestamp, // Return timestamp for display
    isStale, // Indicate if showing stale/cached data while fetching
  };
}

export function useBalanceHistory(days = 30) {
  const { data, error, isLoading, mutate } = useSWR(
    `/analytics/balance-history?days=${days}`,
    swrFetcher
  );

  return {
    history: data?.snapshots || [],
    count: data?.count || 0,
    isLoading,
    isError: error,
    error,
    refresh: mutate,
  };
}

export default useBalances;

